// src/api.js

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://quiron.app/api';

export const api = axios.create({
  baseURL: BASE_URL,
});

let onSessionExpired = null;

export const registerSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      if (onSessionExpired) {
        onSessionExpired();
      }
    }
    return Promise.reject(error);
  }
);

const handleError = (error, context = 'operação') => {
  const errorDetails = {
    message: error.message,
    status: error.response?.status,
    responseData: error.response?.data,
    request: error.request ? 'Nenhuma resposta recebida' : undefined,
    timestamp: new Date().toISOString(),
  };
  console.error(`Erro na requisição para ${context}:`, errorDetails);

  if (error.response) {
    if (error.response.status === 401) {
      throw new Error('Sua sessão expirou. Por favor, faça o login novamente.');
    }
    const detail = error.response.data?.detail || error.response.data?.error || `Erro desconhecido no servidor ao realizar ${context}`;
    throw new Error(`Erro ${error.response.status}: ${detail}`);
  } else if (error.request) {
    throw new Error(`Erro de rede ao realizar ${context}: o servidor não respondeu.`);
  } else if (error.code === 'ECONNABORTED') {
    throw new Error(`Timeout ao realizar ${context}: a requisição demorou mais que o esperado.`);
  } else {
    throw new Error(`Erro de configuração ao realizar ${context}: ${error.message}`);
  }
};

export const getDraftConsultation = async (data) => {
  try {
    const response = await api.post('/consultations/draft', data, {
      timeout: 180000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'gerar rascunho');
  }
};

export const deleteChatSession = async (sessionId) => {
  try {
    const response = await api.delete(`/neuralweb/sessions/${sessionId}`, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'excluir sessão de chat');
  }
};

export const getUserPreferences = async () => {
  try {
    const response = await api.get('/settings/preferences', {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'buscar preferências do usuário');
  }
};

export const updateUserPreferences = async (preferencesData) => {
  try {
    const response = await api.put('/settings/preferences', preferencesData, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'atualizar preferências do usuário');
  }
};

export const getAnamnesisTemplates = async () => {
  try {
    const response = await api.get('/settings/anamnesis-templates', {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'carregar templates de anamnese');
  }
};

export const getSummary = async (improvedText) => {
  try {
    const response = await api.post('/consultations/summary', { improvedText }, {
      timeout: 180000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'gerar resumo');
  }
};

export const generateAvatar = async (prompt, isTemporary = false) => {
  const endpoint = isTemporary ? '/generate-avatar-temp' : '/generate-avatar';
  try {
    const response = await api.post(endpoint, { prompt }, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'gerar avatar');
  }
};

export const getUserInfo = async () => {
  try {
    const response = await api.get('/user/info', {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'buscar dados do usuário');
  }
};

export const createConsultation = async (data) => {
  try {
    const response = await api.post('/consultations', data, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'criar consulta');
  }
};

export const getAllConsultations = async (config = {}) => {
  try {
    const response = await api.get('/consultations', {
      ...config,
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'buscar consultas');
  }
};

export const getConsultationById = async (id) => {
  try {
    const response = await api.get(`/consultations/${id}`, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'buscar consulta por ID');
  }
};

export const getChatSessions = async () => {
  try {
    const response = await api.get('/neuralweb/sessions');
    return response.data;
  } catch (error) {
    handleError(error, 'buscar histórico de conversas');
  }
};

export const getChatMessages = async (sessionId) => {
  try {
    const response = await api.get(`/neuralweb/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    handleError(error, 'carregar mensagens da conversa');
  }
};

export const sendChatMessage = async (message, includeClinicalReasoning = false, fileDetails = null, signal = null, sessionId = null, libraryId = null) => {
  try {
    let response;
    if (fileDetails && fileDetails.file) {
      const formData = new FormData();
      formData.append('message', message);
      formData.append('include_reasoning', String(includeClinicalReasoning));
      
      if (fileDetails.type === 'text_file') {
        formData.append('text_file', fileDetails.file);
      } else if (fileDetails.type === 'image_file') {
        formData.append('image_file', fileDetails.file);
      }

      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      if (libraryId) {
        formData.append('library_id', libraryId);
      }
      response = await api.post('/neuralweb', formData, {
        timeout: 180000,
        signal: signal,
      });
    } else {
      const payload = {
        message: message,
        include_reasoning: includeClinicalReasoning,
        session_id: sessionId,
        library_id: libraryId,
      };
      response = await api.post('/neuralweb', payload, {
        timeout: 180000,
        signal: signal,
        headers: { 'Content-Type': 'application/json' }
      });
    }
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Requisição para /neuralweb cancelada.');
      throw new Error('Canceled');
    }
    handleError(error, 'enviar mensagem de chat');
  }
};

export const uploadFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/academic/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'fazer upload de arquivo acadêmico');
  }
};

export const processFile = async (data) => {
  try {
    const response = await api.post('/academic/process', data, {
      timeout: 180000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'processar arquivo acadêmico');
  }
};

export const updateUserProfile = async (profileData) => {
  try {
    const response = await api.put('/user/update', profileData, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'atualizar perfil');
  }
};

export const deleteConsultations = async (ids) => {
  try {
    const response = await api.delete('/consultations', {
      data: { ids },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'excluir consultas');
  }
};

export const submitFeedback = async (feedbackData) => {
  try {
    const response = await api.post('/feedback', feedbackData, {
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'enviar feedback');
  }
};

export const createLibrary = async (name) => {
  try {
    const response = await api.post('/academic/libraries', { name });
    return response.data;
  } catch (error) {
    handleError(error, 'criar biblioteca');
  }
};

export const getLibraries = async () => {
  try {
    const response = await api.get('/academic/libraries');
    return response.data;
  } catch (error) {
    handleError(error, 'buscar bibliotecas');
  }
};

export const deleteLibrary = async (libraryId) => {
  try {
    const response = await api.delete(`/academic/libraries/${libraryId}`);
    return response.data;
  } catch (error) {
    handleError(error, 'excluir biblioteca');
  }
};

export const uploadDocumentToLibrary = async (libraryId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await api.post(`/academic/libraries/${libraryId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'fazer upload de documento');
  }
};

export const getLibraryDocuments = async (libraryId) => {
  try {
    const response = await api.get(`/academic/libraries/${libraryId}/documents`);
    return response.data;
  } catch (error) {
    handleError(error, 'buscar documentos da biblioteca');
  }
};